import { inject, Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { HttpService } from '../http/http.service';
import { BillingStatus } from '../../models/billing/billing-status.model';
import { CreateCheckoutSessionInput } from '../../models/billing/create-checkout-session-input.model';
import {
  ChangeSubscriptionInput,
  ChangeSubscriptionResult,
  SubscriptionChangePreview,
} from '../../models/billing/change-subscription.model';
import { SubscriptionPlan } from '../../enums/subscription-plan.enum';

@Injectable({ providedIn: 'root' })
export class BillingService {
  private readonly http = inject(HttpService);
  private readonly BASE_URL = 'billing';

  createCheckoutSession(body: CreateCheckoutSessionInput): Observable<{ url: string }> {
    return this.http.post<{ url: string }>(`${this.BASE_URL}/checkout`, body);
  }

  createPortalSession(body: { returnUrl: string }): Observable<{ url: string }> {
    return this.http.post<{ url: string }>(`${this.BASE_URL}/portal`, body);
  }

  getBillingStatus(): Observable<BillingStatus> {
    return this.http.get<BillingStatus>(`${this.BASE_URL}/status`);
  }

  /** Change plan and/or interval for an existing (Stripe-managed) subscription. */
  changeSubscription(body: ChangeSubscriptionInput): Observable<ChangeSubscriptionResult> {
    return this.http.post<ChangeSubscriptionResult, ChangeSubscriptionInput>(
      `${this.BASE_URL}/change-subscription`,
      body,
    );
  }

  /** Dry-run a subscription change to show the exact cost before confirming. */
  previewChange(
    plan: SubscriptionPlan,
    interval: 'month' | 'year',
  ): Observable<SubscriptionChangePreview> {
    return this.http.get<SubscriptionChangePreview>(
      `${this.BASE_URL}/change-subscription/preview`,
      {
        params: { plan, interval },
      },
    );
  }

  /** Undo a scheduled cancellation so the subscription renews normally again. */
  resumeSubscription(): Observable<{ cancelAtPeriodEnd: boolean }> {
    return this.http.post<{ cancelAtPeriodEnd: boolean }>(
      `${this.BASE_URL}/resume-subscription`,
      {},
    );
  }
}
